import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../components/ui/command';
import { ChevronDown, ChevronUp, Search, FileText, Download, Plus, ChevronsUpDown, Check, Camera } from 'lucide-react';
import { cn } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PackageManagement = ({ token, clients, onNavigate }) => {
  const [trackings, setTrackings] = useState([]);
  const [filteredTrackings, setFilteredTrackings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedTrackings, setSelectedTrackings] = useState([]);

  const handleUploadPhoto = (trackingId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(`${API}/admin/packages/${trackingId}/images`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        alert('Foto subida exitosamente');
      } catch (err) {
        alert('Error: ' + (err.response?.data?.detail || err.message));
      }
    };
    input.click();
  };
  
  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    searchType: 'todos',
    location: 'cualquiera',
    shippingType: 'todos',
    userId: 'todos',
    repackId: 'todos',
    dateFrom: '',
    dateTo: '',
    consolidated: 'todos',
    status: 'todos',
    agency: 'todos',
    agent: 'todos'
  });
  
  const [showUnprocessed, setShowUnprocessed] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterClientComboOpen, setFilterClientComboOpen] = useState(false);

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    fetchTrackings();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [trackings, filters, showUnprocessed]);

  const fetchTrackings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/trackings`, axiosConfig);
      setTrackings(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...trackings];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(t =>
        t.tracking_number?.toLowerCase().includes(searchLower) ||
        t.sender?.toLowerCase().includes(searchLower) ||
        t.recipient?.toLowerCase().includes(searchLower)
      );
    }

    // Location filter
    if (filters.location !== 'cualquiera') {
      filtered = filtered.filter(t => t.location === filters.location);
    }

    // Shipping type filter
    if (filters.shippingType !== 'todos') {
      filtered = filtered.filter(t => t.shipping_type === filters.shippingType);
    }

    // User filter
    if (filters.userId !== 'todos') {
      filtered = filtered.filter(t => t.client_id === filters.userId);
    }

    // Status filter
    if (filters.status !== 'todos') {
      filtered = filtered.filter(t => t.status === filters.status);
    }

    // Consolidated filter
    if (filters.consolidated !== 'todos') {
      const isConsolidated = filters.consolidated === 'si';
      filtered = filtered.filter(t => t.consolidated === isConsolidated);
    }

    // Agency filter
    if (filters.agency !== 'todos') {
      filtered = filtered.filter(t => t.agency === filters.agency);
    }

    // Show only unprocessed
    if (showUnprocessed) {
      filtered = filtered.filter(t => !t.processed);
    }

    setFilteredTrackings(filtered);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    const sorted = [...filteredTrackings].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setSortConfig({ key, direction });
    setFilteredTrackings(sorted);
  };

  const toggleRow = (trackingId) => {
    setExpandedRows(prev => ({
      ...prev,
      [trackingId]: !prev[trackingId]
    }));
  };

  const toggleSelectTracking = (trackingId) => {
    setSelectedTrackings(prev =>
      prev.includes(trackingId)
        ? prev.filter(id => id !== trackingId)
        : [...prev, trackingId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedTrackings.length === filteredTrackings.length) {
      setSelectedTrackings([]);
    } else {
      setSelectedTrackings(filteredTrackings.map(t => t.id));
    }
  };

  const handleBatchStatusUpdate = async (newStatus) => {
    if (selectedTrackings.length === 0) {
      alert('Selecciona al menos un paquete');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/trackings/batch-update`,
        {
          tracking_ids: selectedTrackings,
          update_data: { status: newStatus, processed: true }
        },
        axiosConfig
      );
      alert(`${selectedTrackings.length} paquetes actualizados`);
      setSelectedTrackings([]);
      fetchTrackings();
    } catch (error) {
      alert('Error al actualizar paquetes: ' + (error.response?.data?.detail || error.message));
    }
  };

  // TAREA 2: Función para eliminar paquetes en lote
  const handleBatchDelete = async () => {
    if (selectedTrackings.length === 0) {
      alert('Selecciona al menos un paquete para eliminar');
      return;
    }

    // Diálogo de confirmación de advertencia
    const confirmMessage = `⚠️ ADVERTENCIA: Eliminación Permanente ⚠️\n\n¿Estás seguro de eliminar ${selectedTrackings.length} paquete(s)?\n\nEsta acción NO se puede deshacer y eliminará:\n- Todos los datos del paquete\n- El historial de tracking\n\n¿Deseas continuar?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    // Segunda confirmación para mayor seguridad
    if (!window.confirm(`Confirma nuevamente: Eliminar ${selectedTrackings.length} paquete(s) de forma permanente.`)) {
      return;
    }

    try {
      const response = await axios.post(
        `${API}/admin/trackings/batch-delete`,
        { tracking_ids: selectedTrackings },
        axiosConfig
      );
      
      alert(`✅ ${response.data.deleted_count || selectedTrackings.length} paquete(s) eliminado(s) exitosamente`);
      setSelectedTrackings([]);
      fetchTrackings();
    } catch (error) {
      alert('Error al eliminar paquetes: ' + (error.response?.data?.detail || error.message));
    }
  };


  const handleConfirmReception = async (trackingId, trackingNumber) => {
    if (!confirm(`¿Confirmas que el paquete ${trackingNumber} ha llegado físicamente al almacén?`)) {
      return;
    }
    
    try {
      await axios.put(
        `${API}/admin/trackings/${trackingId}/confirm-reception`,
        {},
        axiosConfig
      );
      alert(`✅ Paquete ${trackingNumber} confirmado como recibido en el almacén.\n\nAhora aparecerá disponible para el cliente en "Solicitar Reempaque".`);
      fetchTrackings();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCreateWR = async () => {
    if (selectedTrackings.length === 0) {
      alert('Selecciona al menos un tracking');
      return;
    }

    if (!window.confirm(`¿Crear Warehouse Receipt con ${selectedTrackings.length} trackings seleccionados?`)) {
      return;
    }

    try {
      // Send as array directly to match FastAPI List[str] parameter
      const response = await axios.post(
        `${API}/admin/trackings/create-warehouse-receipt`,
        selectedTrackings,  // Send array of IDs directly
        {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            'Content-Type': 'application/json'
          }
        }
      );
      alert(`✅ Warehouse Receipt creado!\n\nWR: ${response.data.wr_number}\nTrackings: ${response.data.tracking_count}\nPeso total: ${response.data.total_weight_lb} lb`);
      setSelectedTrackings([]);
      fetchTrackings();
    } catch (error) {
      alert('Error al crear WR: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleChangeLocation = async (newLocation) => {
    if (selectedTrackings.length === 0) {
      alert('Selecciona al menos un tracking');
      return;
    }

    const locationLabels = {
      miami: 'Miami',
      en_transito: 'En Tránsito',
      venezuela: 'Venezuela',
      entregado: 'Entregado'
    };

    if (!window.confirm(`¿Cambiar ubicación de ${selectedTrackings.length} trackings a "${locationLabels[newLocation]}"?`)) {
      return;
    }

    try {
      const response = await axios.put(
        `${API}/admin/trackings-batch/update-location`,
        {
          tracking_ids: selectedTrackings,
          location: newLocation
        },
        axiosConfig
      );
      alert(`✅ ${response.data.updated_count} trackings actualizados a "${locationLabels[newLocation]}"`);
      setSelectedTrackings([]);
      fetchTrackings();
    } catch (error) {
      alert('Error al cambiar ubicación: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.full_name : 'Desconocido';
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return <span className="text-gray-400">↕</span>;
    return sortConfig.direction === 'asc' ? <span>↑</span> : <span>↓</span>;
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText size={24} />
            Gestión de Paquetes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filters Section */}
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div className="col-span-2">
              <Label>Búsqueda</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Número de Paquete / Remitente / Destinatario"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Label>Tipo de Búsqueda</Label>
              <Select value={filters.searchType} onValueChange={(v) => setFilters({ ...filters, searchType: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="tracking">Solo Tracking</SelectItem>
                  <SelectItem value="remitente">Solo Remitente</SelectItem>
                  <SelectItem value="destinatario">Solo Destinatario</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Ubicación</Label>
              <Select value={filters.location} onValueChange={(v) => setFilters({ ...filters, location: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cualquiera">Cualquiera</SelectItem>
                  <SelectItem value="miami">Miami</SelectItem>
                  <SelectItem value="venezuela">Venezuela</SelectItem>
                  <SelectItem value="en_transito">En Tránsito</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Tipo de Envío</Label>
              <Select value={filters.shippingType} onValueChange={(v) => setFilters({ ...filters, shippingType: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="aereo">Aéreo</SelectItem>
                  <SelectItem value="maritimo">Marítimo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Cliente</Label>
              <Popover open={filterClientComboOpen} onOpenChange={setFilterClientComboOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="filter-pkg-client-selector">
                    {filters.userId !== 'todos'
                      ? (() => { const c = clients?.find(cl => cl.id === filters.userId); return c ? c.full_name : 'Todos'; })()
                      : 'Todos'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar por nombre, email o locker..." data-testid="filter-pkg-client-search" />
                    <CommandList>
                      <CommandEmpty>No se encontró cliente.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem value="todos-mostrar-todos" onSelect={() => { setFilters({ ...filters, userId: 'todos' }); setFilterClientComboOpen(false); }}>
                          <Check className={cn("mr-2 h-4 w-4", filters.userId === 'todos' ? "opacity-100" : "opacity-0")} />
                          Todos
                        </CommandItem>
                        {clients.map(client => (
                          <CommandItem
                            key={client.id}
                            value={`${client.full_name} ${client.email} ${client.locker_number || ''}`}
                            onSelect={() => { setFilters({ ...filters, userId: client.id }); setFilterClientComboOpen(false); }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", filters.userId === client.id ? "opacity-100" : "opacity-0")} />
                            {client.full_name} <span className="text-xs text-muted-foreground ml-1">{client.locker_number || client.email}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label>Estatus</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="pending">Pendiente</SelectItem>
                  <SelectItem value="in_warehouse">En Almacén</SelectItem>
                  <SelectItem value="in_repack">En Reempaque</SelectItem>
                  <SelectItem value="shipped">Enviado</SelectItem>
                  <SelectItem value="delivered">Entregado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Consolidado</Label>
              <Select value={filters.consolidated} onValueChange={(v) => setFilters({ ...filters, consolidated: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="si">Sí</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Agencia</Label>
              <Select value={filters.agency} onValueChange={(v) => setFilters({ ...filters, agency: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="principal">Principal</SelectItem>
                  <SelectItem value="secundaria">Secundaria</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => alert('Función de reportes en desarrollo.\n\nPodrás exportar:\n- Lista de paquetes a Excel/PDF\n- Reporte de paquetes por estado\n- Reporte de paquetes por cliente')}
              >
                <FileText className="mr-2" size={16} />
                Reportes
              </Button>

              <Button 
                variant="outline" 
                size="sm"
                onClick={handleCreateWR}
                disabled={selectedTrackings.length === 0}
              >
                Crear WR
              </Button>

              <select
                className="px-3 py-2 border rounded-md text-sm"
                onChange={(e) => {
                  if (e.target.value) {
                    handleChangeLocation(e.target.value);
                    e.target.value = '';
                  }
                }}
              >
                <option value="">Cambiar Ubicación</option>
                <option value="miami">Miami</option>
                <option value="en_transito">En Tránsito</option>
                <option value="venezuela">Venezuela</option>
                <option value="entregado">Entregado</option>
              </select>

              <select
                className="px-3 py-2 border rounded-md text-sm"
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'delete') {
                    handleBatchDelete();
                  } else if (value) {
                    handleBatchStatusUpdate(value);
                  }
                  e.target.value = '';
                }}
              >
                <option value="">Acciones en Lote</option>
                <option value="in_warehouse">Marcar En Almacén</option>
                <option value="shipped">Marcar Enviado</option>
                <option value="delivered">Marcar Entregado</option>
                <option value="delete" style={{color: 'red'}}>🗑️ Eliminar Seleccionados</option>
              </select>

              <Button variant="outline" size="sm" disabled={selectedTrackings.length === 0}>
                <Download className="mr-2" size={16} />
                Descargar Etiquetas
              </Button>

              <Button variant="outline" size="sm" disabled={selectedTrackings.length === 0}>
                <Download className="mr-2" size={16} />
                Descargar PDFs
              </Button>
            </div>

            <div className="ml-auto flex items-center gap-2">
              <Label>Solo no procesados</Label>
              <button
                onClick={() => setShowUnprocessed(!showUnprocessed)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  showUnprocessed ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    showUnprocessed ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="p-2">
                    <input
                      type="checkbox"
                      checked={selectedTrackings.length === filteredTrackings.length && filteredTrackings.length > 0}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th className="p-2 text-left">Número</th>
                  <th className="p-2 text-left">Procesado</th>
                  <th className="p-2 text-left cursor-pointer" onClick={() => handleSort('created_at')}>
                    Fecha <SortIcon column="created_at" />
                  </th>
                  <th className="p-2 text-left">Remitente</th>
                  <th className="p-2 text-left">Destinatario</th>
                  <th className="p-2 text-left">Agencia</th>
                  <th className="p-2 text-left">Estatus</th>
                  <th className="p-2 text-left cursor-pointer" onClick={() => handleSort('tracking_number')}>
                    Tracking <SortIcon column="tracking_number" />
                  </th>
                  <th className="p-2 text-left cursor-pointer" onClick={() => handleSort('weight')}>
                    Peso <SortIcon column="weight" />
                  </th>
                  <th className="p-2 text-left cursor-pointer" onClick={() => handleSort('volumetric_weight')}>
                    Pvol <SortIcon column="volumetric_weight" />
                  </th>
                  <th className="p-2 text-left">Medidas</th>
                  <th className="p-2 text-left">Ubicación</th>
                  <th className="p-2 text-left cursor-pointer" onClick={() => handleSort('volume')}>
                    Volumen <SortIcon column="volume" />
                  </th>
                  <th className="p-2 text-left">Acción</th>
                </tr>
              </thead>
              <tbody>
                {filteredTrackings.map((tracking) => (
                  <React.Fragment key={tracking.id}>
                    <tr className="border-b hover:bg-gray-50">
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={selectedTrackings.includes(tracking.id)}
                          onChange={() => toggleSelectTracking(tracking.id)}
                        />
                      </td>
                      <td className="p-2">
                        <button onClick={() => toggleRow(tracking.id)} className="text-blue-600">
                          {expandedRows[tracking.id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                        <span className="ml-2 font-mono text-xs">{tracking.id.substring(0, 8)}</span>
                      </td>
                      <td className="p-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          tracking.processed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {tracking.processed ? 'Procesado' : 'No Procesado'}
                        </span>
                      </td>
                      <td className="p-2 text-xs">{new Date(tracking.created_at).toLocaleString()}</td>
                      <td className="p-2">{tracking.sender || '-'}</td>
                      <td className="p-2">
                        <button onClick={() => onNavigate && onNavigate('clients', { filterClientId: tracking.client_id })} className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer">
                          {getClientName(tracking.client_id)}
                        </button>
                      </td>
                      <td className="p-2">{tracking.agency || '-'}</td>
                      <td className="p-2">
                        <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">
                          {tracking.status}
                        </span>
                      </td>
                      <td className="p-2 font-mono text-xs">{tracking.tracking_number}</td>
                      <td className="p-2">{tracking.weight ? `${tracking.weight} lb` : '-'}</td>
                      <td className="p-2">{tracking.volumetric_weight ? `${tracking.volumetric_weight}` : '-'}</td>
                      <td className="p-2">{tracking.dimensions || '-'}</td>
                      <td className="p-2">{tracking.location || '-'}</td>
                      <td className="p-2">{tracking.volume ? `${tracking.volume} ft³` : '-'}</td>
                      <td className="p-2">
                        <div className="flex gap-1">
                          {tracking.status === 'pending_reception' && (
                            <Button
                              size="sm"
                              onClick={() => handleConfirmReception(tracking.id, tracking.tracking_number)}
                              className="bg-green-600 hover:bg-green-700 text-white text-xs"
                              title="Confirmar que el paquete llegó físicamente al almacén"
                            >
                              ✓ Confirmar Recepción
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              window.open(`${API}/admin/trackings/${tracking.id}/label`, '_blank');
                            }}
                            className="text-xs"
                            title="Descargar Etiqueta"
                          >
                            🏷️ Etiqueta
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUploadPhoto(tracking.id)}
                            className="text-xs"
                            title="Agregar foto del paquete"
                            data-testid={`upload-photo-${tracking.id}`}
                          >
                            <Camera size={12} className="mr-1" /> Foto
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {expandedRows[tracking.id] && (
                      <tr className="bg-gray-50">
                        <td colSpan="14" className="p-4">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p><strong>Descripción:</strong> {tracking.description || 'N/A'}</p>
                              <p><strong>Tipo de Envío:</strong> {tracking.shipping_type}</p>
                              <p><strong>Consolidado:</strong> {tracking.consolidated ? 'Sí' : 'No'}</p>
                            </div>
                            <div>
                              <p><strong>Agente:</strong> {tracking.agent || 'N/A'}</p>
                              <p><strong>Creado:</strong> {new Date(tracking.created_at).toLocaleString()}</p>
                              <p><strong>Actualizado:</strong> {new Date(tracking.updated_at).toLocaleString()}</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            {filteredTrackings.length === 0 && (
              <div className="text-center py-12">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <p className="text-gray-500 mt-2">No se encontraron paquetes</p>
              </div>
            )}
          </div>

          <div className="mt-4 text-sm text-gray-600">
            Mostrando {filteredTrackings.length} de {trackings.length} paquetes
            {selectedTrackings.length > 0 && ` | ${selectedTrackings.length} seleccionados`}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PackageManagement;
